import { useState, useEffect, useCallback } from "react";
import { useAuthContext } from "./authContext";
import { reportError } from "../utils/reportError";
import type { AppError } from "../utils/errors";

export interface LicenseState {
  active: boolean;
  loading: boolean;
  error: AppError | null;
  refresh: () => void;
  startCheckout: (inviteCode: string) => Promise<void>;
  redeemToken: (token: string) => Promise<{ success: boolean }>;
}

export function useLicense(): LicenseState {
  const { session, license } = useAuthContext();
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AppError | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!session) {
      setActive(false);
      setLoading(false);
      setError(null);
      return;
    }
    let mounted = true;
    setLoading(true);
    setError(null);
    license
      .checkLicense(session.userId)
      .then((status) => {
        if (mounted) {
          setActive(status.active);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (mounted) {
          setError(reportError(err, "LICENSE_CHECK_FAILED"));
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [session?.userId, tick]);

  const refresh = useCallback(() => setTick((n) => n + 1), []);

  const startCheckout = useCallback(
    async (inviteCode: string) => {
      const { checkoutUrl } = await license.startCheckout(inviteCode);
      window.location.href = checkoutUrl;
    },
    [license],
  );

  const redeemToken = useCallback(
    (token: string) => license.redeemToken(token),
    [license],
  );

  return { active, loading, error, refresh, startCheckout, redeemToken };
}
